/**
 * <product-gallery>
 * PDP main image gallery + thumbnail strip, native scroll-snap.
 *
 * - Main track: horizontal scroll-snap (one slide per viewport)
 * - Thumb strip: click thumb -> main scrolls to that slide
 * - Pagination dots: rendered from JS, click jumps slide
 * - Click on main image: pendulum cycle (next until last, then prev until first, repeat)
 * - Variant switching: update(variant) swaps in the related product's gallery
 *   media (or falls back to the main product's), mirroring what
 *   product-gallery-swiper.liquid renders server-side on first paint.
 * - Public API:
 *     update(variant)
 *     slideTo(index, instant=false)
 *     setSlides(mainHtmlList: string[], thumbHtmlList: string[])
 *     get currentIndex()
 *     get slideCount()
 */

// Matches `| image_url: width: 1200` in snippets/media.liquid.
const MEDIA_IMAGE_WIDTH = 1200;

// TODO: move to assets/ and pass via data-play-icon so the URL isn't hardcoded.
// Kept in sync with the same URL in snippets/media.liquid for now.
const DEFAULT_PLAY_ICON = "https://cdn.shopify.com/s/files/1/1782/4093/files/play-video.png";

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector("[data-pg-main]");
    this.mainTrack = this.querySelector("[data-pg-main-track]");
    this.thumbTrack = this.querySelector("[data-pg-thumb-track]");
    this.pagination = this.querySelector("[data-pg-pagination]");
    if (!this.mainTrack) return;

    this.controller = new AbortController();
    if (this.hasAttribute("data-desktop-grid")) {
      this.desktopGridQuery = window.matchMedia("(min-width: 768px)");
      this.bindDesktopScrollOrder();
      this.desktopGridQuery.addEventListener("change", () => {
        // Clear the previous mode's scroll axis before positioning the active media.
        if (this.isDesktopGrid) this.mainTrack.scrollLeft = 0;
        else {
          const scroller = this.closest(".product-details-layout__media");
          if (scroller) scroller.scrollTop = 0;
        }
        this.startMainObserver();
        this.slideTo(this.activeIndex, true);
      }, { signal: this.controller.signal });
    }
    window.addEventListener(
      "enroute:variant-changed",
      (event) => {
        const variant = event?.detail?.variant;
        if (variant) this.update(variant);
      },
      { signal: this.controller.signal }
    );
    this.activeIndex = 0;
    this.slideLabel = this.dataset.labelSlide || "Slide";
    this.playIcon = this.dataset.playIcon || DEFAULT_PLAY_ICON;
    // Fingerprint of the media set currently in the DOM. Seeded by Liquid so
    // update() can tell "already showing this" from "needs a rebuild".
    this.mediaSignature = this.dataset.mediaSignature || "";

    // Defensive: reset scroll position (iOS RTL inheritance / mandatory snap quirks).
    this.mainTrack.scrollLeft = 0;
    if (this.thumbTrack) this.thumbTrack.scrollLeft = 0;

    this.refreshRefs();
    this.bindMainClick();
    this.bindThumbClick();
    this.bindPaginationClick();
    this.startMainObserver();
    this.setActive(0);

    // Deep-link (?variant=) landing slide, computed by Liquid.
    const initialIndex = Number(this.dataset.initialIndex) || 0;
    if (initialIndex > 0) this.slideTo(initialIndex, true);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.mainObserver?.disconnect();
  }

  refreshRefs() {
    this.mainSlides = Array.from(this.querySelectorAll("[data-pg-slide]"));
    this.thumbItems = Array.from(this.querySelectorAll("[data-pg-thumb]"));
    this.bullets = Array.from(this.querySelectorAll("[data-pg-bullet]"));
  }

  get currentIndex() {
    return this.activeIndex;
  }

  get isDesktopGrid() {
    return this.desktopGridQuery?.matches || false;
  }

  get slideCount() {
    return this.mainSlides.length;
  }

  bindDesktopScrollOrder() {
    const scroller = this.closest('.product-details-new-2610 .product-details-layout__media');
    if (!scroller) return;

    scroller.addEventListener('wheel', (event) => {
      // Keep native downward chaining and mobile/horizontal/zoom gestures.
      if (!this.isDesktopGrid || !event.cancelable || event.defaultPrevented || event.ctrlKey ||
          event.deltaY >= 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      const pageTop = window.scrollY;
      if (pageTop <= 0) return;

      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      const delta = event.deltaY * unit;
      event.preventDefault();
      window.scrollBy({ top: Math.max(delta, -pageTop), behavior: 'instant' });

      // A wheel step crossing the page top continues inside the gallery.
      const remainder = delta + pageTop;
      if (remainder < 0) scroller.scrollBy({ top: remainder, behavior: 'instant' });
    }, { passive: false, signal: this.controller.signal });
  }

  bindMainClick() {
    if (!this.main) return;
    this.main.addEventListener(
      "click",
      (e) => {
        if (this.isDesktopGrid) return;
        // Ignore clicks on interactive children (links, video controls, etc.)
        if (e.target.closest("a, video, iframe, button")) return;
        if (this.slideCount <= 1) return;
        const rect = this.mainTrack.getBoundingClientRect();
        const isLeftHalf = e.clientX - rect.left < rect.width / 2;
        this.slideTo(this.activeIndex + (isLeftHalf ? -1 : 1));
      },
      { signal: this.controller.signal }
    );
  }

  bindThumbClick() {
    if (!this.thumbTrack) return;
    this.thumbTrack.addEventListener(
      "click",
      (e) => {
        const thumb = e.target.closest("[data-pg-thumb]");
        if (!thumb) return;
        const idx = this.thumbItems.indexOf(thumb);
        if (idx >= 0) this.slideTo(idx);
      },
      { signal: this.controller.signal }
    );
  }

  bindPaginationClick() {
    if (!this.pagination) return;
    this.pagination.addEventListener(
      "click",
      (e) => {
        const bullet = e.target.closest("[data-pg-bullet]");
        if (!bullet) return;
        const idx = this.bullets.indexOf(bullet);
        if (idx >= 0) this.slideTo(idx);
      },
      { signal: this.controller.signal }
    );
  }

  startMainObserver() {
    this.mainObserver?.disconnect();
    if (this.isDesktopGrid) return;
    if (this.mainSlides.length === 0) return;
    this.mainObserver = new IntersectionObserver(
      (entries) => {
        // Pick the entry with highest intersection ratio.
        let best = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        }
        if (!best) return;
        const idx = this.mainSlides.indexOf(best.target);
        if (idx >= 0 && idx !== this.activeIndex) this.setActive(idx);
      },
      { root: this.mainTrack, threshold: 0.6 }
    );
    this.mainSlides.forEach((slide) => this.mainObserver.observe(slide));
  }

  setActive(index) {
    this.activeIndex = index;
    this.bullets.forEach((b, i) => b.classList.toggle("is-active", i === index));
    this.thumbItems.forEach((t, i) => t.classList.toggle("is-active", i === index));

    // Scroll thumb strip so the active thumb is visible.
    const activeThumb = this.thumbItems[index];
    if (!this.isDesktopGrid && activeThumb && this.thumbTrack) {
      const tRect = this.thumbTrack.getBoundingClientRect();
      const aRect = activeThumb.getBoundingClientRect();
      if (aRect.left < tRect.left || aRect.right > tRect.right) {
        activeThumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  }

  slideTo(index, instant = false) {
    if (index < 0 || index >= this.slideCount) return;
    const target = this.mainSlides[index];
    if (!target) return;
    if (this.isDesktopGrid) {
      const targetRect = target.getBoundingClientRect();
      const alignBottom = index >= this.slideCount - 2;
      const scroller = this.closest(".product-details-layout__media");
      const overflowY = scroller && window.getComputedStyle(scroller).overflowY;
      const scrollsIndependently = scroller && /^(auto|scroll)$/.test(overflowY) &&
        scroller.scrollHeight > scroller.clientHeight;
      if (scrollsIndependently) {
        scroller.scrollTo({
          top: (alignBottom ? targetRect.bottom - scroller.clientHeight : targetRect.top) -
            scroller.getBoundingClientRect().top - scroller.clientTop + scroller.scrollTop,
          behavior: instant ? "auto" : "smooth",
        });
      } else {
        // The 2610 desktop grid flows with the page rather than inside a
        // scrollable media column. Its vertical navigation must move the page.
        window.scrollTo({
          top: (alignBottom ? targetRect.bottom - window.innerHeight : targetRect.top) + window.scrollY,
          behavior: instant ? "auto" : "smooth",
        });
      }
      this.setActive(index);
      return;
    }
    // Use integer offsetLeft (instead of scrollIntoView) so the final scroll
    // position lands on a whole pixel — avoids sub-pixel bleed where the
    // neighboring slide peeks through on mandatory snap.
    this.mainTrack.scrollTo({
      left: Math.round(target.offsetLeft),
      behavior: instant ? "auto" : "smooth",
    });
    // Optimistic update so callers can read currentIndex immediately.
    this.setActive(index);
  }

  setSlides(mainHtmlList, thumbHtmlList) {
    if (!Array.isArray(mainHtmlList) || !Array.isArray(thumbHtmlList)) return;
    if (this.mainTrack) this.mainTrack.innerHTML = mainHtmlList.join("");
    if (this.thumbTrack) this.thumbTrack.innerHTML = thumbHtmlList.join("");
    this.rebuildPagination(mainHtmlList.length);
    this.refreshRefs();
    this.startMainObserver();
    this.activeIndex = 0;
    if (this.mainTrack) this.mainTrack.scrollLeft = 0;
    if (this.isDesktopGrid) {
      const scroller = this.closest(".product-details-layout__media");
      if (scroller) scroller.scrollTop = 0;
    }
    this.setActive(0);
  }

  rebuildPagination(count) {
    if (!this.pagination) return;
    if (count <= 1) {
      this.pagination.innerHTML = "";
      return;
    }
    let html = "";
    for (let i = 0; i < count; i++) {
      html += `<button type="button" class="pg-bullet" data-pg-bullet aria-label="${this.slideLabel} ${i + 1}"></button>`;
    }
    this.pagination.innerHTML = html;
  }

  /**
   * Point the gallery at the media set for `variant`. Driven by the variant
   * switcher's enroute:variant-changed event; also callable directly.
   *
   * @param {object} variant - a variant entry from product-info-json
   */
  update(variant) {
    const { list, isRelated } = this.resolveMedia(variant);
    if (list.length === 0) return;

    const signature = list.map((media) => media.id).join(",");
    if (signature !== this.mediaSignature) {
      const { main, thumb } = this.buildSlides(list);
      this.setSlides(main, thumb);
      this.mediaSignature = signature;
    }

    // featured_media_position indexes the MAIN product's media, so it means
    // nothing while a related product's gallery is on screen. Without a
    // related gallery, locate the variant's media inside the main list
    // (falling back to featured_media_position) and scroll to it.
    if (!isRelated) {
      const index = this.findVariantMediaIndex(variant, list);
      // Smooth so the media column visibly scrolls up/down to the image on the
      // 2610 layout (mobile keeps its horizontal slide animation).
      if (index >= 0) this.slideTo(index);
    }
  }

  /**
   * Locate featured media by ID first; older product data can fall back to
   * the image URL or its 1-based position in the main product's media.
   */
  findVariantMediaIndex(variant, list) {
    if (variant?.featured_media_id != null) {
      const index = list.findIndex((media) => String(media.id) === String(variant.featured_media_id));
      if (index >= 0) return index;
    }
    const variantSrc = this.stripSizeParams(variant?.image);
    if (variantSrc) {
      const idx = list.findIndex((media) => {
        const mediaSrc = this.stripSizeParams(media.preview_image || media.src);
        return mediaSrc && mediaSrc === variantSrc;
      });
      if (idx >= 0) return idx;
    }
    const position = Number(variant?.featured_media_position);
    if (Number.isInteger(position) && position >= 1 && position <= list.length) return position - 1;
    return -1;
  }

  stripSizeParams(url) {
    if (!url || typeof url !== "string") return "";
    return url.split(/[?#]/)[0].replace(/^(?:https?:)?\/\//, "");
  }

  productInfo() {
    if (this.productInfoCache === undefined) {
      const scope = this.closest("section.product.wp.new-look") || document;
      this.productInfoCache = window._erj_?.getProductInfo?.(scope) || null;
    }
    return this.productInfoCache;
  }

  resolveMedia(variant) {
    const info = this.productInfo();
    const relatedKey = variant?.related_product_key;
    const related = relatedKey ? info?.related_products?.[relatedKey] : null;
    if (related?.gallery_media?.length > 0) {
      return { list: related.gallery_media, isRelated: true };
    }
    return { list: info?.media || [], isRelated: false };
  }

  buildSlides(list) {
    // DIY products render their first slide through a dedicated wrapper that
    // diy-charm-preview.css targets — leave that path exactly as it was.
    const hasDiy = !!document.querySelector("diy-charm-preview");
    const main = [];
    const thumb = [];
    for (const [index, media] of list.entries()) {
      main.push(this.renderMainSlide(media, index, hasDiy));
      thumb.push(`<div class="pg-thumb" data-pg-thumb>${this.renderThumb(media)}</div>`);
    }
    return { main, thumb };
  }

  renderMainSlide(media, index, hasDiy) {
    if (index === 0 && hasDiy) {
      const arc = document.querySelector(".diy-charms-arc")?.outerHTML || "";
      const img = `<img class="diy-main-image" src="${this.imageUrl(media)}" alt="${media.alt || ""}">`;
      return `<div class="pg-slide" data-pg-slide><div class="diy-charm-preview-container"><div class="swiper-zoom-container">${img}${arc}</div></div></div>`;
    }
    return `<div class="pg-slide" data-pg-slide>${this.renderMedia(media)}</div>`;
  }

  /**
   * Main-slide markup. Mirrors the non-thumbnail branches of snippets/media.liquid
   * so a rebuilt slide is styled by the same CSS as the server-rendered one.
   */
  renderMedia(media) {
    const alt = media.alt || "";
    const src = this.imageUrl(media);
    const id = media.id || "";
    const image = src ? `<div class="slide-image ${this.closest(".product-details-new-2610") ? "pd2610-ratio-image" : "ratio-image"} swiper-zoom-target"><img src="${src}" alt="${alt}"></div>` : "";

    switch (media.media_type || "image") {
      case "image":
        return image;

      case "external_video": {
        // No padding-top wrapper on purpose: .product-single__media already has
        // `aspect-ratio: 1 / 1.25` in customhvg-product.css, and media.liquid's
        // inline padding-top double-counts against it.
        const embed = media.external_video_url;
        if (!embed) return `<div class="product-single__media" data-media-id="${id}">${image}</div>`;
        return `<div class="product-single__media" data-media-id="${id}"><iframe frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="allowfullscreen" src="${embed}" title="${alt}"></iframe></div>`;
      }

      case "video": {
        const sources = (Array.isArray(media.sources) ? media.sources : [])
          .filter((source) => (source.mime_type || "").includes("mp4"))
          .map((source) => `<source src="${source.url || ""}" type="${source.mime_type || "video/mp4"}">`)
          .join("");
        const poster = src ? ` poster="${src}"` : "";
        return `<div class="product-single__media" data-media-id="${id}"><video playsinline="playsinline" autoplay="autoplay" loop="loop" muted="muted" preload="metadata" aria-label="${alt}"${poster}>${sources}</video></div>`;
      }

      default:
        return `<div class="product-single__media" data-media-id="${id}">${image}</div>`;
    }
  }

  /**
   * Thumb markup. Mirrors the `thumbnail: true` branches of snippets/media.liquid
   * (background-image anchors, not <img>) — .slider-nav a supplies the 1:1 box.
   */
  renderThumb(media) {
    const url = this.imageUrl(media);
    if (!url) return "";
    const type = media.media_type || "image";
    if (type === "video" || type === "external_video") {
      return `<div class="product-single__media" data-media-id="${media.id || ""}"><a class="slide-thumb" style="background-image: url('${url}')"><img class="play-video" src="${this.playIcon}" alt="${media.alt || ""}"></a></div>`;
    }
    return `<a style="background-image: url('${url}')"></a>`;
  }

  /**
   * product-info-json stores unsized `image_url` output. Cap the width the same
   * way media.liquid does — full size for gifs (resizing breaks animation).
   */
  imageUrl(media) {
    const raw = media.preview_image || media.src || "";
    if (!raw) return "";
    if (raw.includes(".gif")) return media.src || raw;
    return `${raw}${raw.includes("?") ? "&" : "?"}width=${MEDIA_IMAGE_WIDTH}`;
  }
}

if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
